import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Card,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { IconTile, Pill } from "../../components/ui";
import { accentText, CheckList, DirArrow, FeatureCard, Section, SectionHeading, WidgetMockup } from "./MarketingParts";

function demoUrl() {
  try {
    const runtime = window.__AW_RUNTIME_CONFIG__ || {};
    const base = String(runtime.widgetServerUrl || runtime.apiBaseUrl || window.location.origin).replace(/\/$/, "");
    return `${base}/widget/demo.html`;
  } catch {
    return "/install-guide";
  }
}

/* --------------------------------- Hero --------------------------------- */
function Hero() {
  const { t } = useTranslation();
  return (
    <Box
      component="section"
      aria-labelledby="home-hero-title"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderBottom: "1px solid",
        borderColor: "divider",
        background: (th) =>
          `linear-gradient(180deg, ${th.aw.bg} 0%, ${th.aw.paper} 100%)`,
        "--mkt-dot": (th) => (th.palette.mode === "dark" ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"),
      }}
    >
      <Box className="mkt-grid-bg" aria-hidden="true" sx={{ position: "absolute", inset: 0 }} />
      <Container sx={{ position: "relative", pt: { xs: 6, md: 10 }, pb: { xs: 7, md: 11 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" },
            gap: { xs: 6, md: 6, lg: 8 },
            alignItems: "center",
          }}
        >
          <Box>
            <Pill sx={{ px: 1.5, py: 0.6, fontSize: "0.8rem", mb: 2.5 }}>
              <Box component="span" aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }} />
              {t("mk_hero_eyebrow")}
            </Pill>
            <Typography
              id="home-hero-title"
              variant="h1"
              sx={{ fontSize: { xs: "2.4rem", sm: "3rem", lg: "3.6rem" }, lineHeight: 1.08 }}
            >
              {t("m_home_hero_title_1")}{" "}
              <Box component="span" sx={{ color: "primary.main", display: { md: "block" } }}>
                {t("m_home_hero_title_2")}
              </Box>
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: { xs: "1.05rem", md: "1.15rem" }, lineHeight: 1.75, mt: 2.5, maxWidth: 560 }}>
              {t("m_home_hero_subtitle")}
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
              <Button variant="contained" size="large" component={RouterLink} to="/register" endIcon={<DirArrow />}>
                {t("m_home_cta_primary")}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component="a"
                href={demoUrl()}
                target="_blank"
                rel="noopener"
                startIcon={<PlayCircleOutlineRoundedIcon />}
              >
                {t("m_home_cta_secondary")}
                <Box component="span" sx={visuallyHidden}>
                  {` (${t("mk_new_tab")})`}
                </Box>
              </Button>
            </Stack>

            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: 3.5, display: "flex", flexWrap: "wrap", gap: { xs: 1.25, sm: 2.5 } }}>
              {[t("mk_hero_point_1"), t("mk_hero_point_2"), t("mk_hero_point_3")].map((p) => (
                <Box component="li" key={p} sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary", fontSize: "0.9rem" }}>
                  <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 18, color: "success.main" }} />
                  {p}
                </Box>
              ))}
            </Box>
          </Box>

          <WidgetMockup />
        </Box>
      </Container>
    </Box>
  );
}

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

/* ------------------------------ Trust strip ----------------------------- */
function TrustStrip() {
  const { t } = useTranslation();
  const items = [
    { icon: <VerifiedOutlinedIcon />, label: t("m_home_stats_1") },
    { icon: <LanOutlinedIcon />, label: t("m_home_stats_2") },
    { icon: <SupportAgentOutlinedIcon />, label: t("m_home_stats_3") },
    { icon: <BoltOutlinedIcon />, label: t("m_home_stats_4") },
  ];
  return (
    <Box component="section" aria-label={t("mk_trust_aria")} sx={{ py: { xs: 4, md: 5 }, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
      <Container>
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          {items.map((it) => (
            <Box component="li" key={it.label} sx={{ display: "flex", alignItems: "center", gap: 1.5, justifyContent: { md: "center" } }}>
              <IconTile size={38} tone="neutral" variant="outline">
                {it.icon}
              </IconTile>
              <Typography sx={{ fontWeight: 600, fontSize: { xs: "0.9rem", md: "0.95rem" } }}>{it.label}</Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

/* -------------------------------- Pillars ------------------------------- */
function Pillars() {
  const { t } = useTranslation();
  const pillars = [
    {
      icon: <AccessibilityNewRoundedIcon />,
      tone: "primary",
      title: t("mk_pillar_widget_t"),
      desc: t("mk_pillar_widget_d"),
      chips: [t("mk_pillar_widget_c1"), t("mk_pillar_widget_c2"), t("mk_pillar_widget_c3")],
      to: "/standard",
    },
    {
      icon: <CookieOutlinedIcon />,
      tone: "teal",
      title: t("mk_pillar_privacy_t"),
      desc: t("mk_pillar_privacy_d"),
      chips: [t("mk_pillar_privacy_c1"), t("mk_pillar_privacy_c2"), t("mk_pillar_privacy_c3")],
      to: "/info",
    },
    {
      icon: <FactCheckOutlinedIcon />,
      tone: "success",
      title: t("mk_pillar_compliance_t"),
      desc: t("mk_pillar_compliance_d"),
      chips: [t("mk_pillar_compliance_c1"), t("mk_pillar_compliance_c2"), t("mk_pillar_compliance_c3")],
      to: "/about",
    },
    {
      icon: <AutoAwesomeOutlinedIcon />,
      tone: "violet",
      title: t("m_home_feature_3_title"),
      desc: t("mk_pillar_ai_d"),
      chips: [t("mk_pillar_ai_c1"), t("mk_pillar_ai_c2"), t("mk_pillar_ai_c3")],
      to: "/standard",
    },
  ];

  return (
    <Section id="product" labelledBy="pillars-title">
      <SectionHeading id="pillars-title" eyebrow={t("mk_pillars_eyebrow")} title={t("mk_pillars_title")} subtitle={t("mk_pillars_sub")} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 2, md: 3 } }}>
        {pillars.map((p) => (
          <FeatureCard key={p.title} interactive>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <IconTile tone={p.tone} size={48}>
                {p.icon}
              </IconTile>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h3" sx={{ fontSize: "1.2rem", mb: 1 }}>
                  {p.title}
                </Typography>
                <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>{p.desc}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} sx={{ mt: 2.5, mb: 2.5 }}>
              {p.chips.map((c) => (
                <Box
                  key={c}
                  component="span"
                  sx={{ px: 1.25, py: 0.35, borderRadius: 999, fontSize: "0.78rem", fontWeight: 600, color: "text.secondary", bgcolor: (th) => th.aw.soft, border: "1px solid", borderColor: "divider" }}
                >
                  {c}
                </Box>
              ))}
            </Stack>
            <Box sx={{ mt: "auto" }}>
              <Button component={RouterLink} to={p.to} endIcon={<DirArrow />} sx={{ px: 1, ml: -1, color: accentText }}>
                {t("m_home_learn_more")}
                <Box component="span" sx={visuallyHidden}>
                  {`: ${p.title}`}
                </Box>
              </Button>
            </Box>
          </FeatureCard>
        ))}
      </Box>
    </Section>
  );
}

/* ------------------------------ How it works ---------------------------- */
function HowItWorks() {
  const { t } = useTranslation();
  const steps = [
    { icon: <AddLinkRoundedIcon />, title: t("mk_how_1_t"), desc: t("mk_how_1_d") },
    { icon: <TuneRoundedIcon />, title: t("mk_how_2_t"), desc: t("mk_how_2_d") },
    { icon: <CodeRoundedIcon />, title: t("mk_how_3_t"), desc: t("mk_how_3_d") },
  ];
  return (
    <Section tone="soft" labelledBy="how-title">
      <SectionHeading id="how-title" eyebrow={t("mk_how_eyebrow")} title={t("mk_how_title")} subtitle={t("mk_how_sub")} />
      <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: { xs: 2, md: 3 } }}>
        {steps.map((s, i) => (
          <Box component="li" key={s.title}>
            <FeatureCard>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                <IconTile size={44}>{s.icon}</IconTile>
                <Box
                  aria-hidden="true"
                  sx={{ fontWeight: 800, fontSize: "0.8rem", color: "text.secondary", border: "1px solid", borderColor: "divider", borderRadius: 999, px: 1.25, py: 0.25 }}
                >
                  {String(i + 1).padStart(2, "0")}
                </Box>
              </Stack>
              <Typography variant="h3" sx={{ fontSize: "1.15rem", mb: 1 }}>
                <Box component="span" sx={visuallyHidden}>{`${i + 1}. `}</Box>
                {s.title}
              </Typography>
              <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>{s.desc}</Typography>
            </FeatureCard>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

/* --------------------------------- Install ------------------------------ */
function CodeSnippet() {
  return (
    <Box
      sx={{
        borderRadius: "14px",
        bgcolor: "#0f172a",
        border: "1px solid",
        borderColor: (th) => (th.palette.mode === "dark" ? th.aw.border : "#0f172a"),
        overflow: "hidden",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 2, height: 36, borderBottom: "1px solid rgba(148,163,184,0.18)", direction: "ltr" }}>
        {["#475569", "#475569", "#475569"].map((c, i) => (
          <Box key={i} sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: c }} />
        ))}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ color: "#94a3b8", fontSize: "0.72rem", fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}>index.html</Box>
      </Stack>
      <Box component="pre" className="mkt-code" sx={{ p: 2 }} tabIndex={0} aria-label="HTML snippet">
        <span className="c">{"<!-- Accss Accessibility (non-blocking) -->"}</span>
        {"\n"}
        <span className="k">{"<script>"}</span>
        {"\n"}
        <span className="p">{"  (function (w, d) {"}</span>
        {"\n"}
        <span className="c">{"    // loads after the page, in idle time"}</span>
        {"\n"}
        <span className="k">{"    var "}</span>
        <span className="p">{"s = d.createElement("}</span>
        <span className="s">{'"script"'}</span>
        <span className="p">{");"}</span>
        {"\n"}
        <span className="p">{"    "}</span>
        <span className="p">{"s.src = "}</span>
        <span className="s">{'"https://accss.net/widget/embed.js"'}</span>
        <span className="p">;</span>
        {"\n"}
        <span className="p">{"    s.async = "}</span>
        <span className="k">true</span>
        <span className="p">;</span>
        {"\n"}
        <span className="p">{"    s.setAttribute("}</span>
        <span className="s">{'"data-site-key"'}</span>
        <span className="p">{", "}</span>
        <span className="s">{'"YOUR_SITE_KEY"'}</span>
        <span className="p">{");"}</span>
        {"\n"}
        <span className="p">{"  })(window, document);"}</span>
        {"\n"}
        <span className="k">{"</script>"}</span>
      </Box>
    </Box>
  );
}

function Install() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);

  return (
    <Section id="install" labelledBy="install-title">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" }, gap: { xs: 5, md: 8 }, alignItems: "center" }}>
        <Box>
          <SectionHeading
            id="install-title"
            align="start"
            eyebrow={t("mk_install_eyebrow")}
            title={t("mk_install_title")}
            subtitle={t("mk_install_sub")}
            sx={{ mb: 4 }}
          />
          <CheckList items={[t("mk_install_point_1"), t("mk_install_point_2"), t("mk_install_point_3")]} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
            <Button variant="contained" component={RouterLink} to="/install-guide" endIcon={<DirArrow />}>
              {t("m_nav_install")}
            </Button>
            <Button variant="outlined" component={RouterLink} to="/support">
              {t("m_nav_support")}
            </Button>
          </Stack>
        </Box>

        <Card sx={{ p: { xs: 2, sm: 2.5 }, boxShadow: (th) => th.aw.shadowMd, minWidth: 0 }}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            variant="fullWidth"
            aria-label={t("mk_install_tabs_aria")}
            sx={{ mb: 2, "& .MuiTab-root": { minWidth: 0, px: { xs: 1, sm: 2 }, whiteSpace: "nowrap", fontSize: { xs: "0.82rem", sm: "0.875rem" } } }}
          >
            <Tab id="inst-tab-0" aria-controls="inst-panel-0" icon={<CodeRoundedIcon fontSize="small" />} iconPosition="start" label={t("mk_install_tab_code")} />
            <Tab id="inst-tab-1" aria-controls="inst-panel-1" icon={<ExtensionOutlinedIcon fontSize="small" />} iconPosition="start" label={t("mk_install_tab_wp")} />
          </Tabs>

          <Box role="tabpanel" id="inst-panel-0" aria-labelledby="inst-tab-0" hidden={tab !== 0}>
            {tab === 0 ? (
              <>
                <CodeSnippet />
                <Typography sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 1.75, lineHeight: 1.65 }}>{t("mk_install_code_note")}</Typography>
              </>
            ) : null}
          </Box>
          <Box role="tabpanel" id="inst-panel-1" aria-labelledby="inst-tab-1" hidden={tab !== 1}>
            {tab === 1 ? (
              <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.25 }}>
                {[t("mk_install_wp_1"), t("mk_install_wp_2"), t("mk_install_wp_3")].map((s, i) => (
                  <Box
                    component="li"
                    key={s}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.75, borderRadius: "12px", border: "1px solid", borderColor: "divider", bgcolor: (th) => th.aw.softer }}
                  >
                    <Box aria-hidden="true" sx={{ width: 28, height: 28, flex: "none", borderRadius: "50%", bgcolor: "primary.main", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700, fontSize: "0.85rem" }}>
                      {i + 1}
                    </Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>{s}</Typography>
                  </Box>
                ))}
                <Typography sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 0.5, lineHeight: 1.65 }}>{t("mk_install_wp_note")}</Typography>
              </Box>
            ) : null}
          </Box>
        </Card>
      </Box>

      {/* Resilience */}
      <Card
        sx={{
          mt: { xs: 5, md: 8 },
          p: { xs: 3, md: 4 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
          gap: { xs: 2.5, md: 4 },
          alignItems: "center",
          bgcolor: (th) => th.aw.accentSoft,
          borderColor: (th) => alpha(th.palette.primary.main, 0.22),
          boxShadow: "none",
        }}
      >
        <IconTile size={56} variant="solid">
          <ShieldOutlinedIcon />
        </IconTile>
        <Box>
          <Typography variant="h3" sx={{ fontSize: { xs: "1.2rem", md: "1.35rem" }, mb: 0.75 }}>
            {t("mk_resilience_t")}
          </Typography>
          <Typography sx={{ color: "text.secondary", lineHeight: 1.7, maxWidth: 720 }}>{t("mk_resilience_d")}</Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Pill tone="success" sx={{ py: 0.6, px: 1.5 }}>
            <SpeedOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
            {t("mk_resilience_pill_1")}
          </Pill>
          <Pill sx={{ py: 0.6, px: 1.5 }}>
            <ShieldOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
            {t("mk_resilience_pill_2")}
          </Pill>
        </Stack>
      </Card>
    </Section>
  );
}

/* ------------------------------ Pricing teaser -------------------------- */
function PricingTeaser() {
  const { t } = useTranslation();
  return (
    <Section tone="soft" labelledBy="pricing-teaser-title" sx={{ py: { xs: 7, md: 9 } }}>
      <Card sx={{ p: { xs: 3, md: 5 }, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.3fr 1fr" }, gap: { xs: 3, md: 6 }, alignItems: "center" }}>
        <Box>
          <Typography component="p" sx={{ color: accentText, fontWeight: 700, fontSize: "0.85rem", mb: 1 }}>
            {t("m_pricing_title")}
          </Typography>
          <Typography id="pricing-teaser-title" variant="h2" sx={{ fontSize: { xs: "1.6rem", md: "2rem" } }}>
            {t("mk_pricing_teaser_t")}
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 1.5, lineHeight: 1.7 }}>{t("m_pricing_subtitle")}</Typography>
          <Button variant="contained" size="large" component={RouterLink} to="/pricing" endIcon={<DirArrow />} sx={{ mt: 3 }}>
            {t("mk_pricing_teaser_cta")}
          </Button>
        </Box>
        <CheckList items={[t("m_home_bullets_1"), t("m_home_bullets_2"), t("m_home_bullets_3"), t("m_home_bullets_4")]} />
      </Card>
    </Section>
  );
}

/* ----------------------------------- FAQ -------------------------------- */
function Faq() {
  const { t } = useTranslation();
  const faqs = [1, 2, 3, 4, 5].map((n) => ({ q: t(`mk_faq_${n}_q`), a: t(`mk_faq_${n}_a`) }));
  return (
    <Section labelledBy="faq-title" maxWidth="md">
      <SectionHeading id="faq-title" eyebrow={t("mk_faq_eyebrow")} title={t("mk_faq_title")} />
      <Stack spacing={1.25}>
        {faqs.map((f, i) => (
          <Accordion
            key={f.q}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: (th) => `${th.aw.radius.md}px !important`,
              bgcolor: "background.paper",
              "&:before": { display: "none" },
              "&.Mui-expanded": { boxShadow: (th) => th.aw.shadowSm },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreRoundedIcon />}
              id={`faq-h-${i}`}
              aria-controls={`faq-p-${i}`}
              sx={{ px: 2.5, minHeight: 60, "& .MuiAccordionSummary-content": { my: 1.5 } }}
            >
              <Typography component="h3" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                {f.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails id={`faq-p-${i}`} sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
              <Typography sx={{ color: "text.secondary", lineHeight: 1.75 }}>{f.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Section>
  );
}

/* --------------------------------- Final CTA ---------------------------- */
function FinalCta() {
  const { t } = useTranslation();
  return (
    <Box component="section" aria-labelledby="cta-title" sx={{ pb: { xs: 7, md: 11 }, bgcolor: "background.paper" }}>
      <Container>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: (th) => `${th.aw.radius.xl + 4}px`,
            bgcolor: "primary.main",
            color: "#fff",
            px: { xs: 3, md: 8 },
            py: { xs: 6, md: 8 },
            textAlign: "center",
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: "radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
              maskImage: "radial-gradient(ellipse 60% 70% at 50% 0%, #000 20%, transparent 70%)",
              WebkitMaskImage: "radial-gradient(ellipse 60% 70% at 50% 0%, #000 20%, transparent 70%)",
            }}
          />
          <Box sx={{ position: "relative", maxWidth: 680, mx: "auto" }}>
            <Typography id="cta-title" variant="h2" sx={{ color: "#fff", fontSize: { xs: "1.75rem", md: "2.4rem" } }}>
              {t("m_home_cta_title")}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.92)", mt: 2, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.7 }}>
              {t("m_home_cta_subtitle")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 4 }}>
              <Button
                size="large"
                component={RouterLink}
                to="/register"
                variant="contained"
                sx={{ bgcolor: "#fff", color: "primary.dark", "&:hover": { bgcolor: "#f1f5f9" }, "&:focus-visible": { outlineColor: "#fff" } }}
              >
                {t("m_home_cta_primary")}
              </Button>
              <Button
                size="large"
                component={RouterLink}
                to="/contact"
                variant="outlined"
                sx={{
                  color: "#fff",
                  bgcolor: "transparent",
                  borderColor: "rgba(255,255,255,0.55)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)", borderColor: "#fff" },
                  "&:focus-visible": { outlineColor: "#fff" },
                }}
              >
                {t("m_home_cta_contact")}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default function MarketingHome() {
  return (
    <>
      <Hero />
      <TrustStrip />
      <Pillars />
      <HowItWorks />
      <Install />
      <PricingTeaser />
      <Faq />
      <FinalCta />
    </>
  );
}
